import { Close, GroupAddOutlined } from '@mui/icons-material';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Divider, Grid2, IconButton, ListItem, MenuList, Stack, Step, StepContent, StepLabel, Stepper, Typography, useMediaQuery } from '@mui/material';
import IndieContinueButton from 'components/forms/IndieContinueButton';
import IndieBackButton from 'components/forms/IndieBackButton';
import IndieSaveButton from 'components/forms/IndieSaveButton';
import IndieStaticSelect from 'components/forms/IndieStaticSelect';
import IndieTextField from 'components/forms/IndieTextField';
import SelectOrCreatePhysicalUnitStep from 'components/forms/installations/SelectOrCreatePhysicalUnitStep';
import { CommunityContext } from 'contexts/communities/CommunityContext';
import { Form, Formik } from 'formik';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CommunitySelectSlider from '../../forms/communities/CommunitySelectSlider';
import { IndieLabelAndValue } from './NewCommunityDialog';
import { UserFeedbackContext } from 'contexts/UserFeedbackContext';

const NewCommunityMemberConfigureInstallationDialog = ({ open, onClose, onSuccess, initialCommunity = null, showNewMemberHeader = true }) => {

    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const { community, submitting, setCommunity, createMember } = useContext(CommunityContext);
    const { setAndShowErrorMessage } = useContext(UserFeedbackContext);
    const isMobileVersion = useMediaQuery((theme) => theme.breakpoints.down('lg'));

    const handleClose = () => {
        onClose();
    }

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }

    const handleSubmit = (values, actions) => {
        if (activeStep < validationSchemas.length - 1) {
            handleNext();
        } else {
            // The physical unit is always already persisted by this point — either picked
            // from the existing-installations table, or just created via the dedicated
            // create-physical-unit step — so only its id needs to travel here.
            createMember({
                physical_unit_id: values.physical_unit_id,
                member_id_dgeg: values.member_id_dgeg,
                member_status: values.member_status,
            }).then((response) => {
                if (onSuccess) {
                    onSuccess(response.data);
                }

                actions.resetForm();
                setActiveStep(0);
            }).catch((error) => {
                console.error(error);
                setAndShowErrorMessage(error);
            });

        }
    };

    const initialValues = {
        community: initialCommunity?.id || '',
        member_id_dgeg: '',
        member_status: '',
        physical_unit: null,
        physical_unit_id: null,
    };

    // Community Selection
    const step1ValidationSchema = Yup.object().shape({
        community: Yup.string().required(t("error.forms.mandatory")),
    });

    // Member Data Step
    const step2ValidationSchema = Yup.object().shape({
        member_id_dgeg: Yup.number().typeError(t("error.forms.numeric")).required(t("error.forms.mandatory")),
        member_status: Yup.string().required(t("error.forms.mandatory")),
    });

    // Select or Create Physical Unit Step
    const step3ValidationSchema = Yup.object().shape({
        physical_unit_id: Yup.number().nullable().test(
            "has-physical-unit",
            t("error.forms.mandatory"),
            (value, context) => !!value || !!context.parent.physical_unit?.type
        ),
    });

    const validationSchemas = [
        Yup.object().shape({}),
        step1ValidationSchema,
        step2ValidationSchema,
        step3ValidationSchema,
        Yup.object().shape({})
    ];

    useEffect(() => {
        return () => {
            setActiveStep(0);
        }
    }, []);

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {showNewMemberHeader ? (
                    <Grid2 container>
                        <Grid2 item size={{ xs: 9, sm: 6 }}>
                            <Stack spacing={2} direction="row" alignItems={"center"}>
                                <IconButton onClick={handleClose}>
                                    <Close />
                                </IconButton>
                                <GroupAddOutlined color="primary" />
                                <Typography variant="subtitle1" color="textPrimary">{t("label.button.newMember")}</Typography>
                            </Stack>
                        </Grid2>
                        <Grid2 item size={{ xs: 3, sm: 6 }} alignItems={"center"}>
                        <Box sx={{ display: 'flex', flexGrow: 1, height: "100%", alignItems: "center", justifyContent: "flex-end"}}>
                                <Stepper activeStep={0} orientation="horizontal">
                                    <Step key={0}>
                                        <StepLabel>{!isMobileVersion && t("label.title.configureInstallation")}</StepLabel>
                                    </Step>
                                    <Step key={1}>
                                        <StepLabel>{!isMobileVersion && t("label.title.configureClient")}</StepLabel>
                                    </Step>
                                    <Step key={2}>
                                        <StepLabel>{!isMobileVersion && t("label.title.configureTariffPlan")}</StepLabel>
                                    </Step>
                                </Stepper>
                            </Box>
                        </Grid2>
                    </Grid2>
                ) : (
                    <Stack spacing={2} direction="row" alignItems="center">
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                        <GroupAddOutlined color="primary" />
                        <Typography variant="subtitle1" color="textPrimary">{t("label.title.configureInstallation")}</Typography>
                    </Stack>
                )}
            </DialogTitle>
            <DialogContent>
                <Divider fullWidth sx={{ mb: 2 }} />
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchemas[activeStep]}
                    validateOnBlur={false}
                    validateOnChange={false}
                    enableReinitialize={true}
                    onSubmit={handleSubmit}
                >    
                    {(formik) => (
                        <Fragment>
                            <Form>
                                <Stepper activeStep={activeStep} orientation="vertical">
                                    <Step key={0}>
                                        <StepLabel>{t("label.step.newCommunityMember.introduction")}</StepLabel>
                                        <StepContent>
                                            <Stack spacing={2}>
                                                <Typography variant="subtitle2">{t("label.step.newCommunityMember.introduction.subtitle")}</Typography>
                                                <Typography variant="body2">
                                                    <Trans i18nKey="label.step.newCommunityMember.introduction.text" components={{ ul: <MenuList />, li: <ListItem /> }} />
                                                </Typography>

                                                <Stack direction="row" spacing={2}>
                                                    <IndieContinueButton id="next_step_button" variant="contained" type="submit" />
                                                </Stack>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                    <Step key={1}>
                                        <StepLabel>{t("label.step.newCommunityMember.selectCommunityStep")}</StepLabel>
                                        <StepContent>
                                            <Box maxWidth={"lg"}>
                                                <Stack spacing={2}>
                                                    {!initialCommunity && (
                                                        <Typography variant="subtitle2">{t("label.step.newCommunityMember.selectCommunityStep.subtitle")}</Typography>
                                                    )}
                                                    {initialCommunity ? (
                                                        <Alert width={"md"} severity="success" variant="outlined">{`${t("newTarriffPlan.initialCommunityData")} ${initialCommunity.name}`}</Alert>
                                                    ) : (
                                                        <Stack direction={"row"} spacing={2}>
                                                            <CommunitySelectSlider
                                                                name="community"
                                                                onChange={(c) => setCommunity(c)}
                                                                additionalFilters={[
                                                                    { id: "business_type", value: "communities" }
                                                                ]}
                                                            />
                                                        </Stack>
                                                    )}
                                                    <Stack direction="row" spacing={2}>
                                                        <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} />
                                                        <IndieContinueButton id="next_step_button" variant="contained" type="submit" />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                    <Step key={2}>
                                        <StepLabel>{t("label.step.newCommunityMember.memberDataStep")}</StepLabel>
                                        <StepContent>
                                            <Stack spacing={2}>
                                                <Typography variant="subtitle2">{t("label.step.newCommunityMember.memberDataStep.subtitle")}</Typography>
                                                <IndieTextField id="member_id_dgeg" name="member_id_dgeg" label={t("label.form.dgegid")} maxLength={100} />
                                                <IndieStaticSelect id="member_status" name="member_status" label={t("label.form.status")} options={
                                                    [
                                                        { value: 'prerecord', label: t("label.status.member.prerecord") },
                                                        { value: 'registered', label: t("label.status.member.registered") },
                                                        { value: 'active', label: t("label.status.member.active") },
                                                        { value: 'suspended', label: t("label.status.member.suspended") },
                                                    ]} sx={{ width: 200 }} />
                                                <Stack direction="row" spacing={2}>
                                                    <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} />
                                                    <IndieContinueButton id="next_step_button" variant="contained" type="submit" />
                                                </Stack>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                    <Step key={3}>
                                        <StepLabel>{t("label.step.newCommunityMember.selectOrCreatePhysicalUnitStep")}</StepLabel>
                                        <StepContent>
                                            <Box maxWidth="lg">
                                                <Stack spacing={2}>
                                                    <Typography variant="subtitle2">{t("label.step.newCommunityMember.selectOrCreatePhysicalUnitStep.subtitle")}</Typography>
                                                    <SelectOrCreatePhysicalUnitStep />
                                                    <Stack direction="row" spacing={2}>
                                                        <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} />
                                                        <IndieContinueButton id="next_step_button" variant="contained" type="submit" />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                    <Step key={4}>
                                        <StepLabel>{t("label.step.newCommunityMember.confirmationStep")}</StepLabel>
                                        <StepContent>
                                            <Stack spacing={4}>
                                                <Typography variant="subtitle2">{t("label.step.newCommunityMember.confirmationStep.subtitle")}</Typography>
                                                <Stack spacing={2}>
                                                    <Stack spacing={1}>
                                                        <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityMember.selectCommunityStep")}</Typography>
                                                        <Divider />
                                                        <IndieLabelAndValue label={t("label.form.community")} value={community?.name} />
                                                    </Stack>
                                                    <Stack spacing={1}>
                                                        <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityMember.memberDataStep")}</Typography>
                                                        <Divider />
                                                        <IndieLabelAndValue label={t("label.form.dgegid")} value={formik.values.member_id_dgeg} />
                                                        <IndieLabelAndValue label={t("label.form.status")} value={t(`label.status.member.${formik.values.member_status}`)} />
                                                    </Stack>
                                                    <Stack spacing={1}>
                                                        <Typography variant='subtitle2' color='textSecondary'>{t("label.step.selectInstallation")}</Typography>
                                                        <Divider />
                                                        <IndieLabelAndValue label={t("label.form.consumptionDeliveryPoint")} value={formik.values.physical_unit?.consumption_deliverypoint} />
                                                        <IndieLabelAndValue label={t("label.form.productionDeliveryPoint")} value={formik.values.physical_unit?.production_deliverypoint} />
                                                        <IndieLabelAndValue label={t("label.form.zipCode")} value={formik.values.physical_unit?.zip_code} />
                                                        <IndieLabelAndValue label={t("label.form.address")} value={formik.values.physical_unit?.address} />
                                                    </Stack>
                                                    <Stack direction="row" spacing={2}>
                                                        <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} disabled={submitting} />
                                                        <IndieSaveButton id="save_button" variant="contained" type="submit" loading={submitting} disabled={submitting} />
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                </Stepper>
                                {/**<pre>{JSON.stringify(formik.errors)}</pre>*/}
                            </Form>
                        </Fragment>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    )
}

export default NewCommunityMemberConfigureInstallationDialog